import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../data/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-ink/10 bg-sand/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src="/assets/logo-wide.png"
              alt="Studio Solutions LLC"
              className="h-12 w-auto sm:h-14"
            />
            <span className="sr-only">Studio Solutions LLC</span>
          </Link>

          <nav className="hidden items-center gap-5 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}
                className={({ isActive }) =>
                  `font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                    isActive ? "text-gold-dark" : "text-ink/70 hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/start-a-project"
              data-testid="nav-cta-start-project"
              className="ml-1 bg-ink px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-gold hover:text-ink"
            >
              Start a Project
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            className="text-ink xl:hidden"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[90] flex flex-col bg-night text-sand"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex h-20 items-center justify-between px-4 sm:px-8">
              <span className="flex items-center gap-2.5">
                <img src="/assets/logo-badge-dark.png" alt="Studio Solutions" className="h-10 w-10 rounded-full" />
                <span className="font-display text-lg font-extrabold tracking-tight">Studio Solutions</span>
              </span>
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="text-sand"
              >
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-6 sm:px-10" aria-label="Mobile">
              {[...NAV_LINKS, { to: "/start-a-project", label: "Start a Project" }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-center justify-between border-b border-sand/10 py-4"
                  >
                    <span className="font-display text-3xl font-bold tracking-tight transition-colors duration-300 group-hover:text-gold sm:text-4xl">
                      {l.label}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-gold/70">
                        0{i + 1}
                      </span>
                      <ArrowUpRight size={20} className="text-gold" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </nav>
            <p className="px-6 pb-8 font-mono text-[10px] uppercase tracking-[0.3em] text-sand/40 sm:px-10">
              Wisconsin · Creative Production Company
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
